/* ═══════════════════════════════════════════════════════════
   SECTION DECK — shared viewport-locked one-page-deck primitives
   ═══════════════════════════════════════════════════════════ */

/* ──────────── PAGE / SCROLL CONTAINER ──────────── */
.deck {
  position: fixed;
  top: var(--nav-height);
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: scroll;
  overflow-x: hidden;
  /* Scroll is JS-driven — see SectionDeck.tsx */
  overscroll-behavior: contain;
  font-size: var(--text-scale, 1rem);
  scrollbar-width: none;
}

.deck::-webkit-scrollbar { display: none; }

/* ──────────── SECTION ──────────── */
.section {
  height: 100%;
  min-height: 100%;
  display: flex;
  /* `safe` keeps the TOP of over-tall content reachable. Plain centring pushes
     the overflow out of both ends of the box and the first line becomes
     unscrollable. Older engines ignore the second declaration and keep the
     first. */
  align-items: center;
  align-items: safe center;
  justify-content: center;
  /* Horizontal padding only. A scroll container's bottom padding is not
     counted in scrollHeight, so vertical padding here would make the last
     line of an overflowing section permanently unreachable. It lives on
     .sectionInner instead, where it is part of the scrollable content. */
  padding: 0 clamp(1.25rem, 4vw, 4rem);
  /* A section taller than the viewport scrolls ITSELF; the deck only advances
     once it has hit the edge (see canScrollWithin in SectionDeck.tsx). This was
     `overflow: hidden`, which silently clipped anything past the fold — on a
     phone that hid several hundred px of copy and screenshots with no way to
     reach them. */
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: none;
  position: relative;
}

.section::-webkit-scrollbar {
  display: none;
}

.sectionInner {
  width: 100%;
  max-width: 1180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(0.6rem, 1.4vh, 1.4rem);
  padding: clamp(1rem, 3vh, 2.4rem) 0;
}

/* ──────────── PROGRESS DOTS (right edge) ──────────── */
.progressRail {
  position: fixed;
  top: 50%;
  right: clamp(0.6rem, 1.5vw, 1.2rem);
  transform: translateY(-50%);
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  pointer-events: auto;
}

.progressDot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  border: none;
  cursor: pointer;
  padding: 0;
  transition: background 0.25s ease, transform 0.25s ease, height 0.25s ease;
}

.progressDot:hover {
  background: hsla(262, 85%, 62%, 0.5);
  transform: scale(1.2);
}

.progressDotActive {
  background: var(--accent-primary);
  height: 24px;
  border-radius: 4px;
  box-shadow: 0 0 12px hsla(262, 85%, 62%, 0.6);
}

/* ──────────── BACK TO TOP BUTTON ──────────── */
.backToTop {
  margin-top: 1.4rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: 1px solid var(--color-border-default);
  color: var(--color-text-secondary);
  padding: 0.6rem 1.1rem;
  border-radius: 100px;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.25s ease, color 0.25s ease, transform 0.25s ease, background 0.25s ease;
}

.backToTop:hover {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
  background: hsla(262, 85%, 62%, 0.06);
  transform: translateY(-2px);
}

.backToTopIcon {
  width: 14px;
  height: 14px;
  display: grid;
  place-items: center;
}

/* ──────────── SCROLL HINT (hero bottom) ──────────── */
.scrollHint {
  position: absolute;
  bottom: clamp(0.6rem, 2vh, 1.4rem);
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.66rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  opacity: 0.7;
  pointer-events: none;
}

.scrollHintMouse {
  width: 20px;
  height: 32px;
  border: 1.5px solid var(--color-text-secondary);
  border-radius: 12px;
  position: relative;
}

.scrollHintMouse::after {
  content: "";
  position: absolute;
  top: 5px;
  left: 50%;
  width: 3px;
  height: 6px;
  background: var(--accent-primary);
  border-radius: 2px;
  transform: translateX(-50%);
  animation: deckScrollDot 1.6s ease-in-out infinite;
}

@keyframes deckScrollDot {
  0%   { transform: translate(-50%, 0); opacity: 1; }
  60%  { transform: translate(-50%, 10px); opacity: 0; }
  100% { transform: translate(-50%, 0); opacity: 1; }
}

/* ──────────── DESIGN TUNER ──────────── */
.tuner {
  position: fixed;
  top: calc(var(--nav-height) + 1rem);
  right: clamp(0.5rem, 1.5vw, 1rem);
  z-index: 100;
  width: 280px;
  font-family: var(--font-body);
}

.tunerToggle {
  position: absolute;
  top: 0;
  right: 0;
  background: hsla(262, 85%, 62%, 0.1);
  border: 1px solid hsla(262, 85%, 62%, 0.3);
  color: var(--accent-primary);
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  backdrop-filter: blur(8px);
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.tunerToggle:hover { background: hsla(262, 85%, 62%, 0.18); }

.tunerPanel {
  margin-top: 2.6rem;
  background: rgba(15, 15, 18, 0.92);
  border: 1px solid var(--color-border-default);
  border-radius: 12px;
  padding: 1rem;
  backdrop-filter: blur(16px);
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  max-height: 70vh;
  overflow-y: auto;
}

.tunerTitle {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent-primary);
}

.tunerRow {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.tunerLabel {
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  color: var(--color-text-secondary);
}

.tunerLabel span:last-child {
  color: var(--accent-primary);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.tunerSlider {
  -webkit-appearance: none;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  outline: none;
}

.tunerSlider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--accent-primary);
  cursor: pointer;
  box-shadow: 0 0 10px hsla(262, 85%, 62%, 0.6);
}

.tunerSlider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--accent-primary);
  cursor: pointer;
  border: none;
}

.tunerSegment {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.3rem;
  padding: 0.2rem;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
}

.tunerSegBtn {
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  padding: 0.4rem 0.2rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tunerSegBtn:hover { color: var(--color-text-primary); }

.tunerSegBtnActive {
  background: hsla(262, 85%, 62%, 0.15);
  color: var(--accent-primary);
  box-shadow: inset 0 0 0 1px hsla(262, 85%, 62%, 0.3);
}

.tunerHint {
  font-size: 0.7rem;
  color: var(--color-text-secondary);
  opacity: 0.7;
  line-height: 1.5;
  margin-top: 0.2rem;
}

@media (max-width: 880px) {
  .tuner { width: 240px; right: 0.5rem; }
  .progressRail { right: 0.5rem; }
}
